import { useEffect, useState } from 'react'
import { Column, Row } from 'react-table'
import Table from '../../../global/components/table/Table'
import { handleColumnFilterUpdate } from '../../../global/components/table/helpers/multiColumnFilters'
import retrieveData from '../../../global/components/table/helpers/retrieveData'
import { formatDateYyyyMmDd } from '../../../global/utils/date/date-utils-yyyy-mm-dd'
import BasicFilter from '../../../global/components/table/helpers/filters/BasicFilter'
import CheckboxFilter from '../../../global/components/table/helpers/filters/CheckboxFilter'
import DateFilter from '../../../global/components/table/helpers/filters/DateFilter'
import DropdownFilter from '../../../global/components/table/helpers/filters/DropdownFilter'
import IncidentReportModal from '../modal/IncidentReportModal'
import PrimaryButton from '../../../global/components/buttons/primary-button/PrimaryButton'
import {
  INCIDENT_TYPES_ARRAY,
  REVIEW_CHANNELS_ARRAY,
  SEVERITY_OF_INCIDENT_ARRAY,
  INCIDENT_REPORT_STATUS_ARRAY,
} from '../../../global/constants/incident-report'
import { formatStatus, formatIncidentType } from '../../utils/format-status'

interface IIncidentReportTableData {
  objectId: string
  incidentDate: Date
  technician?: {
    name: string
    location?: {
      name: string
    }
  }
  order?: {
    workOrderId: number
    status: string | null
    tireRackOrders?: {
      orderNumber: string | null
    }[]
    orderVehicles?: {
      mileage: number | null
    }[]
  }
  van?: {
    name: string
  }
  incidentType: string | null
  severityOfIncident: string | null
  isLostTimeInjury: boolean
  reviewChannel: string | null
  reviewScore: number
  incidentOverviewAndResolution: string | null
  qualifyingDefects: boolean
  dtClaims: boolean
  wheelOff: boolean
  wheelOffReason: string | null
  isAccident: boolean
  accidentData: {
    driverName?: string
    cityState?: string
    numberOfInjuries?: number
    numberOfFatalities?: number
    isVehicleTowed?: boolean
    isHazmatRelease?: boolean
    isDriverCitation?: boolean
  } | null
  status: string | null
  assignedTo: string | null
}

function IncidentReportsTable() {
  const [tableState, setTableState] = useState({
    data: [] as IIncidentReportTableData[],
    isLoading: false,
    pageSize: 100,
    offSet: 0,
    count: 0,
  })
  const [columnFilters, setColumnFilters] = useState({})
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedIncidentReportObjectId, setSelectedIncidentReportObjectId] =
    useState<string | undefined>(undefined)

  // Filter state variables
  const [incidentDateFilter, setIncidentDateFilter] = useState('')
  const [monthFilter, setMonthFilter] = useState('')
  const [yearFilter, setYearFilter] = useState('')
  const [incidentTypeFilter, setIncidentTypeFilter] = useState('')
  const [severityFilter, setSeverityFilter] = useState('')
  const [reviewChannelFilter, setReviewChannelFilter] = useState('')
  const [isLostTimeInjuryChecked, setIsLostTimeInjuryChecked] = useState(false)
  const [qualifyingDefectsChecked, setQualifyingDefectsChecked] = useState(false)
  const [dtClaimsChecked, setDtClaimsChecked] = useState(false)
  const [wheelOffChecked, setWheelOffChecked] = useState(false)
  const [isAccidentChecked, setIsAccidentChecked] = useState(false)
  const [vehicleTowedChecked, setVehicleTowedChecked] = useState(false)
  const [hazmatReleasedChecked, setHazmatReleasedChecked] = useState(false)
  const [driverCitationChecked, setDriverCitationChecked] = useState(false)
  const [onsiteCancellationChecked, setOnsiteCancellationChecked] = useState(false)
  const [statusFilter, setStatusFilter] = useState('')

  // Dropdown items
  const monthItems = [
    { key: '01', value: 'Jan' },
    { key: '02', value: 'Feb' },
    { key: '03', value: 'Mar' },
    { key: '04', value: 'Apr' },
    { key: '05', value: 'May' },
    { key: '06', value: 'Jun' },
    { key: '07', value: 'Jul' },
    { key: '08', value: 'Aug' },
    { key: '09', value: 'Sep' },
    { key: '10', value: 'Oct' },
    { key: '11', value: 'Nov' },
    { key: '12', value: 'Dec' },
  ]

  const currentYear = new Date().getFullYear()
  const yearItems = Array.from({ length: 10 }, (_, i) => {
    const year = (currentYear - i).toString()
    return { key: year, value: year }
  })

  const incidentTypeItems = INCIDENT_TYPES_ARRAY.map((type) => ({
    key: type,
    value: formatIncidentType(type),
  }))

  const severityItems = SEVERITY_OF_INCIDENT_ARRAY.map((severity) => ({
    key: severity,
    value: severity,
  }))

  const reviewChannelItems = REVIEW_CHANNELS_ARRAY.map((channel) => ({
    key: channel,
    value: channel.replace(/_/g, ' '),
  }))

  const statusItems = INCIDENT_REPORT_STATUS_ARRAY.map((status) => ({
    key: status,
    value: formatStatus(status),
  }))

  const endpoint = `${process.env.REACT_APP_ORIGIN}/incident-reports?`

  function retrieveDataWrapper() {
    retrieveData(setTableState, {
      endpoint,
      pageSize: tableState.pageSize,
      offSet: tableState.offSet,
      columnFilters,
    })
  }

  useEffect(retrieveDataWrapper, [
    columnFilters,
    tableState.pageSize,
    tableState.offSet,
  ])

  function onColumnFilterUpdate(filtersUpdate: object) {
    handleColumnFilterUpdate(filtersUpdate, columnFilters, setColumnFilters)
  }

  function handleRowClick(row: Row) {
    const original: any = row.original
    setSelectedIncidentReportObjectId(original.objectId)
    setIsModalOpen(true)
  }

  function handleModalClose() {
    setIsModalOpen(false)
    setSelectedIncidentReportObjectId(undefined)
  }

  function handleSaveSuccess() {
    retrieveDataWrapper()
  }

  function handleCreateNew() {
    setSelectedIncidentReportObjectId(undefined)
    setIsModalOpen(true)
  }

  function getMonth(date: Date | null): string {
    if (!date) return ''
    const d = new Date(date)
    return d.toLocaleString('default', { month: 'short' })
  }

  function getYear(date: Date | null): string {
    if (!date) return ''
    return new Date(date).getFullYear().toString()
  }

  function formatBoolean(value: boolean | undefined): string {
    if (value === undefined || value === null) return ''
    return value ? 'Yes' : 'No'
  }

  const columns: Column<any>[] = [
    {
      Header: 'Status',
      accessor: (data: IIncidentReportTableData) =>
        data.status ? formatStatus(data.status) : '',
      Filter: DropdownFilter({
        value: statusFilter,
        onChange: (val: string) => {
          setStatusFilter(val)
          onColumnFilterUpdate({ status: val })
        },
        items: statusItems,
      }),
    },
    {
      Header: 'Assigned To',
      accessor: (data: IIncidentReportTableData) => data.assignedTo ?? '',
      Filter: BasicFilter({
        onEnter: (val: string) =>
          onColumnFilterUpdate({ assignedTo: val }),
      }),
    },
    {
      Header: 'Date of Incident',
      accessor: (data: IIncidentReportTableData) => {
        const date = data.incidentDate
        return date ? formatDateYyyyMmDd(new Date(date)) : ''
      },
      Filter: DateFilter({
        value: incidentDateFilter,
        onChange: (val: string) => {
          setIncidentDateFilter(val)
          // Send date as YYYY-MM-DD format for Date column filtering
          onColumnFilterUpdate({ incidentDate: val })
        },
      }),
    },
    {
      Header: 'Month',
      accessor: (data: IIncidentReportTableData) => getMonth(data.incidentDate),
      Filter: DropdownFilter({
        value: monthFilter,
        onChange: (val: string) => {
          setMonthFilter(val)
          onColumnFilterUpdate({ incidentDateMonth: val })
        },
        items: monthItems,
      }),
    },
    {
      Header: 'Year',
      accessor: (data: IIncidentReportTableData) => getYear(data.incidentDate),
      Filter: DropdownFilter({
        value: yearFilter,
        onChange: (val: string) => {
          setYearFilter(val)
          onColumnFilterUpdate({ incidentDateYear: val })
        },
        items: yearItems,
      }),
    },
    {
      Header: 'Employee Name',
      accessor: (data: IIncidentReportTableData) => data.technician?.name ?? '',
      Filter: BasicFilter({
        onEnter: (val: string) =>
          onColumnFilterUpdate({ technician: { name: val } }),
      }),
    },
    {
      Header: 'Location',
      accessor: (data: IIncidentReportTableData) =>
        data.technician?.location?.name ?? '',
      Filter: BasicFilter({
        onEnter: (val: string) =>
          onColumnFilterUpdate({ technician: { location: { name: val } } }),
      }),
    },
    {
      Header: 'TR Order #',
      accessor: (data: IIncidentReportTableData) =>
        data.order?.tireRackOrders?.[0]?.orderNumber ?? 'N/A',
      Filter: BasicFilter({
        onEnter: (val: string) =>
          onColumnFilterUpdate({ order: { tireRackOrders: { orderNumber: val } } }),
      }),
    },
    {
      Header: 'Work Order #',
      accessor: (data: IIncidentReportTableData) =>
        data.order?.workOrderId?.toString() ?? '',
      Filter: BasicFilter({
        onEnter: (val: string) =>
          onColumnFilterUpdate({ order: { workOrderId: val } }),
      }),
    },
    {
      Header: 'On-site Cancellation',
      accessor: (data: IIncidentReportTableData) =>
        data.order?.status === 'cancelled_onsite' ? 'Yes' : 'No',
      Filter: CheckboxFilter({
        checked: onsiteCancellationChecked,
        onChange: (checked: boolean) => {
          setOnsiteCancellationChecked(checked)
          onColumnFilterUpdate({
            order: { status: checked ? 'cancelled_onsite' : '' },
          })
        },
      }),
    },
    {
      Header: 'Van #',
      accessor: (data: IIncidentReportTableData) => data.van?.name ?? '',
      Filter: BasicFilter({
        onEnter: (val: string) => onColumnFilterUpdate({ van: { name: val } }),
      }),
    },
    {
      Header: 'Incident Type',
      accessor: (data: IIncidentReportTableData) => data.incidentType ?? '',
      Filter: DropdownFilter({
        value: incidentTypeFilter,
        onChange: (val: string) => {
          setIncidentTypeFilter(val)
          onColumnFilterUpdate({ incidentType: val })
        },
        items: incidentTypeItems,
      }),
    },
    {
      Header: 'Severity',
      accessor: (data: IIncidentReportTableData) => data.severityOfIncident ?? '',
      Filter: DropdownFilter({
        value: severityFilter,
        onChange: (val: string) => {
          setSeverityFilter(val)
          onColumnFilterUpdate({ severityOfIncident: val })
        },
        items: severityItems,
      }),
    },
    {
      Header: 'LTI',
      accessor: (data: IIncidentReportTableData) =>
        formatBoolean(data.isLostTimeInjury),
      Filter: CheckboxFilter({
        checked: isLostTimeInjuryChecked,
        onChange: (checked: boolean) => {
          setIsLostTimeInjuryChecked(checked)
          onColumnFilterUpdate({ isLostTimeInjury: checked ? '1' : '' })
        },
      }),
    },
    {
      Header: 'Review Channel',
      accessor: (data: IIncidentReportTableData) => data.reviewChannel ?? '',
      Filter: DropdownFilter({
        value: reviewChannelFilter,
        onChange: (val: string) => {
          setReviewChannelFilter(val)
          onColumnFilterUpdate({ reviewChannel: val })
        },
        items: reviewChannelItems,
      }),
    },
    {
      Header: 'Review Score',
      accessor: (data: IIncidentReportTableData) =>
        data.reviewScore?.toString() ?? '',
      Filter: BasicFilter({
        onEnter: (val: string) => onColumnFilterUpdate({ reviewScore: val }),
      }),
    },
    {
      Header: 'Summary',
      accessor: (data: IIncidentReportTableData) =>
        data.incidentOverviewAndResolution ?? '',
      Filter: BasicFilter({
        onEnter: (val: string) =>
          onColumnFilterUpdate({ incidentOverviewAndResolution: val }),
      }),
    },
    {
      Header: 'Qualifying Defect',
      accessor: (data: IIncidentReportTableData) =>
        formatBoolean(data.qualifyingDefects),
      Filter: CheckboxFilter({
        checked: qualifyingDefectsChecked,
        onChange: (checked: boolean) => {
          setQualifyingDefectsChecked(checked)
          onColumnFilterUpdate({ qualifyingDefects: checked ? '1' : '' })
        },
      }),
    },
    {
      Header: 'DT Claims',
      accessor: (data: IIncidentReportTableData) =>
        formatBoolean(data.dtClaims),
      Filter: CheckboxFilter({
        checked: dtClaimsChecked,
        onChange: (checked: boolean) => {
          setDtClaimsChecked(checked)
          onColumnFilterUpdate({ dtClaims: checked ? '1' : '' })
        },
      }),
    },
    {
      Header: 'Wheel Off',
      accessor: (data: IIncidentReportTableData) =>
        formatBoolean(data.wheelOff),
      Filter: CheckboxFilter({
        checked: wheelOffChecked,
        onChange: (checked: boolean) => {
          setWheelOffChecked(checked)
          onColumnFilterUpdate({ wheelOff: checked ? '1' : '' })
        },
      }),
    },
    {
      Header: 'Wheel Off Summary',
      accessor: (data: IIncidentReportTableData) => data.wheelOffReason ?? '',
      Filter: BasicFilter({
        onEnter: (val: string) => onColumnFilterUpdate({ wheelOffReason: val }),
      }),
    },
    {
      Header: 'DOT Recordable Accident',
      accessor: (data: IIncidentReportTableData) =>
        formatBoolean(data.isAccident),
      Filter: CheckboxFilter({
        checked: isAccidentChecked,
        onChange: (checked: boolean) => {
          setIsAccidentChecked(checked)
          onColumnFilterUpdate({ isAccident: checked ? '1' : '' })
        },
      }),
    },
    {
      Header: 'Driver Name',
      accessor: (data: IIncidentReportTableData) =>
        data.accidentData?.driverName ?? '',
      Filter: BasicFilter({
        onEnter: (val: string) =>
          onColumnFilterUpdate({ 'accidentData.driverName': val }),
      }),
    },
    {
      Header: 'City/Town, State',
      accessor: (data: IIncidentReportTableData) =>
        data.accidentData?.cityState ?? '',
      Filter: BasicFilter({
        onEnter: (val: string) =>
          onColumnFilterUpdate({ 'accidentData.cityState': val }),
      }),
    },
    {
      Header: '# of Injuries',
      accessor: (data: IIncidentReportTableData) =>
        data.accidentData?.numberOfInjuries?.toString() ?? '',
      Filter: BasicFilter({
        onEnter: (val: string) =>
          onColumnFilterUpdate({ 'accidentData.numberOfInjuries': val }),
      }),
    },
    {
      Header: '# of Fatalities',
      accessor: (data: IIncidentReportTableData) =>
        data.accidentData?.numberOfFatalities?.toString() ?? '',
      Filter: BasicFilter({
        onEnter: (val: string) =>
          onColumnFilterUpdate({ 'accidentData.numberOfFatalities': val }),
      }),
    },
    {
      Header: 'Vehicle Towed',
      accessor: (data: IIncidentReportTableData) =>
        formatBoolean(data.accidentData?.isVehicleTowed),
      Filter: CheckboxFilter({
        checked: vehicleTowedChecked,
        onChange: (checked: boolean) => {
          setVehicleTowedChecked(checked)
          onColumnFilterUpdate({ 'accidentData.isVehicleTowed': checked ? '1' : '' })
        },
      }),
    },
    {
      Header: 'Hazmat Released',
      accessor: (data: IIncidentReportTableData) =>
        formatBoolean(data.accidentData?.isHazmatRelease),
      Filter: CheckboxFilter({
        checked: hazmatReleasedChecked,
        onChange: (checked: boolean) => {
          setHazmatReleasedChecked(checked)
          onColumnFilterUpdate({ 'accidentData.isHazmatRelease': checked ? '1' : '' })
        },
      }),
    },
    {
      Header: 'Driver Citation',
      accessor: (data: IIncidentReportTableData) =>
        formatBoolean(data.accidentData?.isDriverCitation),
      Filter: CheckboxFilter({
        checked: driverCitationChecked,
        onChange: (checked: boolean) => {
          setDriverCitationChecked(checked)
          onColumnFilterUpdate({ 'accidentData.isDriverCitation': checked ? '1' : '' })
        },
      }),
    },
  ]

  return (
    <div style={{ width: '100%' }}>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
        <PrimaryButton
          buttonName='Create Incident Report'
          onClick={handleCreateNew}
        />
      </div>
      <IncidentReportModal
        isOpen={isModalOpen}
        handleClosed={handleModalClose}
        incidentReportObjectId={selectedIncidentReportObjectId}
        onSaveSuccess={handleSaveSuccess}
      />
      <Table
        columns={columns}
        data={tableState.data ?? []}
        isLoading={tableState.isLoading}
        onRowClick={handleRowClick}
        isPaginated
        pagination={{ setTableState, tableState }}
        isTableTopperPresent={false}
      />
    </div>
  )
}

export default IncidentReportsTable
